import { effect as _$effect } from "@opentui/solid";
import { createComponent as _$createComponent } from "@opentui/solid";
import { insert as _$insert } from "@opentui/solid";
import { memo as _$memo } from "@opentui/solid";
import { createTextNode as _$createTextNode } from "@opentui/solid";
import { insertNode as _$insertNode } from "@opentui/solid";
import { setProp as _$setProp } from "@opentui/solid";
import { createElement as _$createElement } from "@opentui/solid";
import { render, useKeyboard, useTerminalDimensions } from "@opentui/solid";
import { createSignal, onMount, For, Show } from "solid-js";
import { aggregateByDate } from "./aggregator.js";
import { loadMessages, loadRecentMessages, loadMessagesIncremental, createCursor, getOpenCodeStoragePath } from "./loader.js";
import { loadMultiAccountQuota, loadMultiAccountThresholds, loadAntigravityQuota } from "./quota-loader.js";
import { loadCodexQuota, loadCodexThresholds } from "./codex-client.js";
const COLORS = {
  bg: {
    primary: "#0a0e1a",
    secondary: "#151b2e",
    accent: "#1e2842"
  },
  text: {
    primary: "#e2e8f0",
    secondary: "#94a3b8",
    muted: "#64748b"
  },
  accent: {
    teal: "#14b8a6",
    amber: "#f59e0b",
    red: "#ef4444",
    cyan: "#06b6d4"
  },
  border: "#334155"
};
const padLeft = (str, width) => str.padStart(width, " ");
const padRight = (str, width) => str.padEnd(width, " ");
const truncateText = (str, width) => {
  if (width <= 0) return "";
  if (str.length <= width) return str;
  if (width <= 3) return str.slice(0, width);
  return `${str.slice(0, width - 3)}...`;
};
function UsageTable(props) {
  const getWindowedData = () => {
    const allEntries = Array.from(props.stats.entries());
    const sortedByDateDesc = allEntries.sort((a, b) => b[0].localeCompare(a[0]));
    const afterScrollOffset = sortedByDateDesc.slice(props.pageOffset);
    const requestedDays = props.daysFilter === 0 ? 10 : props.daysFilter;
    return afterScrollOffset.slice(0, requestedDays);
  };
  const statsArray = () => {
    const windowedData = getWindowedData();
    return windowedData.slice(props.internalScroll, props.internalScroll + props.maxVisibleDays);
  };
  const totalDaysInWindow = () => getWindowedData().length;
  const formatNum = n => n.toLocaleString("en-US");
  const formatCost = c => `$${c.toFixed(2)}`;
  const formatDateRange = () => {
    const windowedData = getWindowedData();
    if (windowedData.length === 0) return "";
    const firstDate = windowedData[0][0];
    const lastDate = windowedData[windowedData.length - 1][0];
    if (props.daysFilter === 1) {
      return firstDate;
    }
    if (props.daysFilter === 7) {
      const start = new Date(firstDate);
      const end = new Date(lastDate);
      const monthName = start.toLocaleDateString("en-US", {
        month: "short"
      });
      return `${monthName} ${start.getDate()}-${end.getDate()}`;
    }
    if (props.daysFilter === 30) {
      const start = new Date(firstDate);
      const end = new Date(lastDate);
      const startMonth = start.toLocaleDateString("en-US", {
        month: "short"
      });
      const endMonth = end.toLocaleDateString("en-US", {
        month: "short"
      });
      if (startMonth === endMonth) {
        return `${startMonth} ${start.getDate()}-${end.getDate()}`;
      }
      return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}`;
    }
    if (firstDate === lastDate) {
      return firstDate;
    }
    const start = new Date(firstDate);
    const end = new Date(lastDate);
    return `${start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    })} - ${end.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    })}`;
  };
  const totalStats = () => {
    const windowedData = getWindowedData();
    let totalTokens = 0;
    let totalCost = 0;
    windowedData.forEach(([_, stat]) => {
      totalTokens += stat.input + stat.output;
      totalCost += stat.cost;
    });
    return {
      tokens: totalTokens,
      cost: totalCost
    };
  };
  return (() => {
    var _el$ = _$createElement("box"),
      _el$2 = _$createElement("box"),
      _el$3 = _$createElement("box"),
      _el$4 = _$createElement("text"),
      _el$5 = _$createElement("b"),
      _el$7 = _$createElement("box"),
      _el$8 = _$createElement("text"),
      _el$9 = _$createElement("b"),
      _el$0 = _$createElement("box"),
      _el$1 = _$createElement("text"),
      _el$10 = _$createElement("span"),
      _el$11 = _$createElement("span"),
      _el$12 = _$createElement("span");
    _$insertNode(_el$, _el$2);
    _$setProp(_el$, "border", true);
    _$setProp(_el$, "borderStyle", "single");
    _$setProp(_el$, "height", "auto");
    _$setProp(_el$, "flexGrow", 1);
    _$setProp(_el$, "padding", 0);
    _$insertNode(_el$2, _el$3);
    _$insertNode(_el$2, _el$7);
    _$insertNode(_el$2, _el$0);
    _$setProp(_el$2, "flexDirection", "column");
    _$insertNode(_el$3, _el$4);
    _$setProp(_el$3, "paddingBottom", 0);
    _$setProp(_el$3, "paddingLeft", 1);
    _$insertNode(_el$4, _el$5);
    _$insertNode(_el$5, _$createTextNode(`■ USAGE BREAKDOWN`));
    _$insert(_el$4, (() => {
      var _c$ = _$memo(() => statsArray().length > 0);
      return () => _c$() && (() => {
        var _el$13 = _$createElement("span"),
          _el$14 = _$createTextNode(` (`),
          _el$16 = _$createTextNode(`)`);
        _$insertNode(_el$13, _el$14);
        _$insertNode(_el$13, _el$16);
        _$insert(_el$13, formatDateRange, _el$16);
        _$effect(_$p => _$setProp(_el$13, "style", {
          fg: COLORS.text.muted
        }, _$p));
        return _el$13;
      })();
    })(), null);
    _$insert(_el$4, (() => {
      var _c$2 = _$memo(() => totalDaysInWindow() > props.maxVisibleDays);
      return () => _c$2() && (() => {
        var _el$17 = _$createElement("span"),
          _el$18 = _$createTextNode(` [`),
          _el$20 = _$createTextNode(`-`),
          _el$21 = _$createTextNode(` of `),
          _el$23 = _$createTextNode(`]`);
        _$insertNode(_el$17, _el$18);
        _$insertNode(_el$17, _el$20);
        _$insertNode(_el$17, _el$21);
        _$insertNode(_el$17, _el$23);
        _$insert(_el$17, () => props.internalScroll + 1, _el$20);
        _$insert(_el$17, () => Math.min(props.internalScroll + props.maxVisibleDays, totalDaysInWindow()), _el$21);
        _$insert(_el$17, totalDaysInWindow, _el$23);
        _$effect(_$p => _$setProp(_el$17, "style", {
          fg: COLORS.text.muted
        }, _$p));
        return _el$17;
      })();
    })(), null);
    _$insert(_el$4, (() => {
      var _c$3 = _$memo(() => !!props.isLoading);
      return () => _c$3() && (() => {
        var _el$24 = _$createElement("span");
        _$insertNode(_el$24, _$createTextNode(` ⟳ Loading...`));
        _$effect(_$p => _$setProp(_el$24, "style", {
          fg: COLORS.accent.amber
        }, _$p));
        return _el$24;
      })();
    })(), null);
    _$insertNode(_el$7, _el$8);
    _$setProp(_el$7, "paddingTop", 1);
    _$setProp(_el$7, "paddingBottom", 1);
    _$setProp(_el$7, "border-bottom", true);
    _$insertNode(_el$8, _el$9);
    _$setProp(_el$8, "wrapMode", "none");
    _$insert(_el$9, () => padRight("DATE", 18), null);
    _$insert(_el$9, () => padLeft("TOKENS", 13), null);
    _$insert(_el$9, () => padLeft("COST", 10), null);
    _$insert(_el$2, _$createComponent(For, {
      get each() {
        return statsArray();
      },
      children: ([dateKey, stat], index) => {
        const isLast = index() === statsArray().length - 1;
        const isEven = index() % 2 === 0;
        const providers = Array.from(stat.providerStats.entries());
        return [(() => {
          var _el$26 = _$createElement("box"),
            _el$27 = _$createElement("text"),
            _el$28 = _$createElement("span"),
            _el$29 = _$createElement("span"),
            _el$30 = _$createElement("span");
          _$insertNode(_el$26, _el$27);
          _$setProp(_el$26, "paddingTop", 0.5);
          _$insertNode(_el$27, _el$28);
          _$insertNode(_el$27, _el$29);
          _$insertNode(_el$27, _el$30);
          _$setProp(_el$27, "overflow", "hidden");
          _$setProp(_el$27, "wrapMode", "none");
          _$insert(_el$28, () => padRight(dateKey, 18));
          _$insert(_el$29, () => padLeft(formatNum(stat.input + stat.output), 13));
          _$insert(_el$30, () => padLeft(formatCost(stat.cost), 10));
          _$effect(_p$ => {
            var _v$1 = providers.length > 0 ? 0.25 : 0.5,
              _v$10 = isEven ? COLORS.bg.secondary : COLORS.bg.accent,
              _v$11 = {
                fg: COLORS.text.primary,
                bold: true
              },
              _v$12 = {
                fg: COLORS.accent.cyan,
                bold: true
              },
              _v$13 = {
                fg: COLORS.accent.amber,
                bold: true
              };
            _v$1 !== _p$.e && (_p$.e = _$setProp(_el$26, "paddingBottom", _v$1, _p$.e));
            _v$10 !== _p$.t && (_p$.t = _$setProp(_el$26, "backgroundColor", _v$10, _p$.t));
            _v$11 !== _p$.a && (_p$.a = _$setProp(_el$28, "style", _v$11, _p$.a));
            _v$12 !== _p$.o && (_p$.o = _$setProp(_el$29, "style", _v$12, _p$.o));
            _v$13 !== _p$.i && (_p$.i = _$setProp(_el$30, "style", _v$13, _p$.i));
            return _p$;
          }, {
            e: undefined,
            t: undefined,
            a: undefined,
            o: undefined,
            i: undefined
          });
          return _el$26;
        })(), _$createComponent(For, {
          each: providers,
          children: ([providerId, providerStat], pIndex) => {
            const isLastProvider = pIndex() === providers.length - 1;
            return (() => {
              var _el$31 = _$createElement("box"),
                _el$32 = _$createElement("text"),
                _el$33 = _$createElement("span"),
                _el$34 = _$createElement("span"),
                _el$35 = _$createElement("span");
              _$insertNode(_el$31, _el$32);
              _$setProp(_el$31, "paddingTop", 0.25);
              _$setProp(_el$31, "paddingBottom", isLastProvider ? 0.5 : 0.25);
              _$setProp(_el$31, "paddingLeft", 2);
              _$insertNode(_el$32, _el$33);
              _$insertNode(_el$32, _el$34);
              _$insertNode(_el$32, _el$35);
              _$setProp(_el$32, "overflow", "hidden");
              _$setProp(_el$32, "wrapMode", "none");
              _$insert(_el$33, () => padRight(`[${providerId}]`, 18));
              _$insert(_el$34, () => padLeft(formatNum(providerStat.input + providerStat.output), 13));
              _$insert(_el$35, () => padLeft(formatCost(providerStat.cost), 10));
              _$effect(_p$ => {
                var _v$14 = isEven ? COLORS.bg.secondary : COLORS.bg.accent,
                  _v$15 = {
                    fg: COLORS.text.muted
                  },
                  _v$16 = {
                    fg: COLORS.accent.cyan
                  },
                  _v$17 = {
                    fg: COLORS.accent.amber
                  };
                _v$14 !== _p$.e && (_p$.e = _$setProp(_el$31, "backgroundColor", _v$14, _p$.e));
                _v$15 !== _p$.t && (_p$.t = _$setProp(_el$33, "style", _v$15, _p$.t));
                _v$16 !== _p$.a && (_p$.a = _$setProp(_el$34, "style", _v$16, _p$.a));
                _v$17 !== _p$.o && (_p$.o = _$setProp(_el$35, "style", _v$17, _p$.o));
                return _p$;
              }, {
                e: undefined,
                t: undefined,
                a: undefined,
                o: undefined
              });
              return _el$31;
            })();
          }
        }), !isLast && (() => {
          var _el$36 = _$createElement("box");
          _$setProp(_el$36, "height", 1);
          _$setProp(_el$36, "border-bottom", true);
          _$effect(_$p => _$setProp(_el$36, "borderColor", COLORS.border, _$p));
          return _el$36;
        })()];
      }
    }), _el$0);
    _$insertNode(_el$0, _el$1);
    _$setProp(_el$0, "paddingTop", 1);
    _$setProp(_el$0, "paddingBottom", 0.5);
    _$setProp(_el$0, "border-top", true);
    _$insertNode(_el$1, _el$10);
    _$insertNode(_el$1, _el$11);
    _$insertNode(_el$1, _el$12);
    _$setProp(_el$1, "wrapMode", "none");
    _$insert(_el$10, () => padRight("TOTAL", 18));
    _$insert(_el$11, () => padLeft(formatNum(totalStats().tokens), 13));
    _$insert(_el$12, () => padLeft(formatCost(totalStats().cost), 10));
    _$effect(_p$ => {
      var _v$ = props.isSelected ? COLORS.accent.teal : COLORS.border,
        _v$2 = props.width ?? "49%",
        _v$3 = COLORS.bg.secondary,
        _v$4 = COLORS.accent.teal,
        _v$5 = COLORS.border,
        _v$6 = COLORS.text.muted,
        _v$7 = COLORS.border,
        _v$8 = {
          fg: COLORS.accent.teal,
          bold: true
        },
        _v$9 = {
          fg: COLORS.accent.cyan,
          bold: true
        },
        _v$0 = {
          fg: COLORS.accent.amber,
          bold: true
        };
      _v$ !== _p$.e && (_p$.e = _$setProp(_el$, "borderColor", _v$, _p$.e));
      _v$2 !== _p$.t && (_p$.t = _$setProp(_el$, "width", _v$2, _p$.t));
      _v$3 !== _p$.a && (_p$.a = _$setProp(_el$, "backgroundColor", _v$3, _p$.a));
      _v$4 !== _p$.o && (_p$.o = _$setProp(_el$4, "fg", _v$4, _p$.o));
      _v$5 !== _p$.i && (_p$.i = _$setProp(_el$7, "borderColor", _v$5, _p$.i));
      _v$6 !== _p$.n && (_p$.n = _$setProp(_el$8, "fg", _v$6, _p$.n));
      _v$7 !== _p$.s && (_p$.s = _$setProp(_el$0, "borderColor", _v$7, _p$.s));
      _v$8 !== _p$.h && (_p$.h = _$setProp(_el$10, "style", _v$8, _p$.h));
      _v$9 !== _p$.r && (_p$.r = _$setProp(_el$11, "style", _v$9, _p$.r));
      _v$0 !== _p$.d && (_p$.d = _$setProp(_el$12, "style", _v$0, _p$.d));
      return _p$;
    }, {
      e: undefined,
      t: undefined,
      a: undefined,
      o: undefined,
      i: undefined,
      n: undefined,
      s: undefined,
      h: undefined,
      r: undefined,
      d: undefined
    });
    return _el$;
  })();
}
function QuotaPanel(props) {
  const renderBar = (used, width = 30) => {
    const filled = Math.round(used * width);
    const empty = width - filled;
    return "█".repeat(filled) + "░".repeat(empty);
  };
  const getColor = used => {
    if (used >= 0.8) return COLORS.accent.red;
    if (used >= 0.5) return COLORS.accent.amber;
    return COLORS.accent.teal;
  };
  const formatResetTime = (resetAt, compact = false) => {
    if (!resetAt) return "";
    const resetDate = new Date(resetAt * 1000);
    const now = new Date();
    if (resetDate <= now) return compact ? "⟳ reset" : "⟳ resetting...";
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfter = new Date(today);
    dayAfter.setDate(dayAfter.getDate() + 2);
    const hours = resetDate.getHours().toString().padStart(2, "0");
    const mins = resetDate.getMinutes().toString().padStart(2, "0");
    if (resetDate >= today && resetDate < tomorrow) {
      return compact ? `↻ ${hours}:${mins}` : `↻ today ${hours}:${mins}`;
    }
    if (resetDate >= tomorrow && resetDate < dayAfter) {
      return compact ? `↻ tmr ${hours}:${mins}` : `↻ tomorrow ${hours}:${mins}`;
    }
    const day = resetDate.getDate().toString().padStart(2, "0");
    const month = (resetDate.getMonth() + 1).toString().padStart(2, "0");
    return compact ? `↻ ${day}.${month}` : `↻ ${day}.${month}. ${hours}:${mins}`;
  };
  const groupedQuotas = () => {
    const groups = new Map();
    props.quotas.forEach(q => {
      if (!groups.has(q.source)) groups.set(q.source, []);
      groups.get(q.source).push(q);
    });
    return Array.from(groups.entries());
  };
  return (() => {
    var _el$37 = _$createElement("box"),
      _el$38 = _$createElement("box"),
      _el$39 = _$createElement("box"),
      _el$40 = _$createElement("text"),
      _el$41 = _$createElement("b");
    _$insertNode(_el$37, _el$38);
    _$setProp(_el$37, "border", true);
    _$setProp(_el$37, "borderStyle", "single");
    _$setProp(_el$37, "height", "auto");
    _$setProp(_el$37, "flexGrow", 1);
    _$setProp(_el$37, "padding", 0);
    _$insertNode(_el$38, _el$39);
    _$setProp(_el$38, "flexDirection", "column");
    _$insertNode(_el$39, _el$40);
    _$setProp(_el$39, "paddingBottom", 0);
    _$setProp(_el$39, "paddingLeft", 1);
    _$insertNode(_el$40, _el$41);
    _$insertNode(_el$41, _$createTextNode(`■ QUOTA STATUS`));
    _$insert(_el$38, _$createComponent(For, {
      get each() {
        return groupedQuotas();
      },
      children: ([source, quotas]) => {
        const groupByAccount = () => {
          const groups = new Map();
          quotas.forEach(q => {
            const accountName = q.label.split(" - ")[0];
            if (!groups.has(accountName)) groups.set(accountName, []);
            groups.get(accountName).push(q);
          });
          return Array.from(groups.entries());
        };
        return (() => {
          var _el$43 = _$createElement("box"),
            _el$44 = _$createElement("box"),
            _el$45 = _$createElement("box"),
            _el$46 = _$createElement("text"),
            _el$47 = _$createElement("b"),
            _el$48 = _$createTextNode(`▸ `);
          _$insertNode(_el$43, _el$44);
          _$setProp(_el$43, "flexDirection", "column");
          _$setProp(_el$43, "paddingTop", 0);
          _$setProp(_el$43, "flexShrink", 0);
          _$setProp(_el$43, "gap", 0);
          _$insertNode(_el$44, _el$45);
          _$setProp(_el$44, "flexShrink", 0);
          _$setProp(_el$44, "paddingTop", 1);
          _$setProp(_el$44, "paddingBottom", 0);
          _$insertNode(_el$45, _el$46);
          _$setProp(_el$45, "flexDirection", "column");
          _$setProp(_el$45, "flexShrink", 0);
          _$insertNode(_el$46, _el$47);
          _$setProp(_el$46, "flexShrink", 0);
          _$setProp(_el$46, "wrapMode", "none");
          _$insertNode(_el$47, _el$48);
          _$insert(_el$47, () => source.toUpperCase(), null);
          _$insert(_el$45, _$createComponent(Show, {
            get when() {
              return source === "anthropic" && props.anthropicThresholds;
            },
            get children() {
              var _el$49 = _$createElement("text");
              _$setProp(_el$49, "flexShrink", 0);
              _$setProp(_el$49, "wrapMode", "none");
              _$insert(_el$49, () => `  thr 5h:${Math.round((props.anthropicThresholds?.session5h ?? 0.7) * 100)}%  w:${Math.round((props.anthropicThresholds?.weekly7d ?? 0.7) * 100)}%  s:${Math.round((props.anthropicThresholds?.weekly7dSonnet ?? 0.7) * 100)}%`);
              _$effect(_$p => _$setProp(_el$49, "fg", COLORS.text.muted, _$p));
              return _el$49;
            }
          }), null);
          _$insert(_el$45, _$createComponent(Show, {
            get when() {
              return source === "codex" && props.codexThresholds;
            },
            get children() {
              var _el$50 = _$createElement("text");
              _$setProp(_el$50, "flexShrink", 0);
              _$setProp(_el$50, "wrapMode", "none");
              _$insert(_el$50, () => `  thr 5h:${Math.round((props.codexThresholds?.fiveHour ?? 0.7) * 100)}%  w:${Math.round((props.codexThresholds?.weekly ?? 0.7) * 100)}%`);
              _$effect(_$p => _$setProp(_el$50, "fg", COLORS.text.muted, _$p));
              return _el$50;
            }
          }), null);
          _$insert(_el$43, _$createComponent(For, {
            get each() {
              const entries = groupByAccount();
              if (!props.twoColumns) {
                return entries.map(entry => [entry]);
              }
              const rows = [];
              for (let i = 0; i < entries.length; i += 2) {
                rows.push(entries.slice(i, i + 2));
              }
              return rows;
            },
            children: row => (() => {
              var _el$51 = _$createElement("box");
              _$insert(_el$51, _$createComponent(For, {
                each: row,
                children: ([accountName, accountQuotas]) => {
                  const isActive = accountName.includes("[ACTIVE]");
                  const cleanName = accountName.replace(" [ACTIVE]", "").trim();
                  const displayAccountName = props.twoColumns ? truncateText(cleanName, isActive ? 15 : 22) : cleanName;
                  return (() => {
                    var _el$52 = _$createElement("box"),
                      _el$53 = _$createElement("box"),
                      _el$54 = _$createElement("text");
                    _$insertNode(_el$52, _el$53);
                    _$setProp(_el$52, "flexDirection", "column");
                    _$setProp(_el$52, "flexShrink", 0);
                    _$setProp(_el$52, "marginBottom", 0);
                    _$setProp(_el$52, "paddingTop", 0);
                    _$setProp(_el$52, "paddingBottom", 0);
                    _$setProp(_el$52, "paddingLeft", 1);
                    _$setProp(_el$52, "paddingRight", 1);
                    _$setProp(_el$52, "marginLeft", 0);
                    _$setProp(_el$52, "marginRight", 0);
                    _$setProp(_el$52, "border", true);
                    _$setProp(_el$52, "borderStyle", "rounded");
                    _$setProp(_el$52, "borderColor", isActive ? "#14b8a6" : "#334155");
                    _$setProp(_el$52, "flexGrow", 1);
                    _$insertNode(_el$53, _el$54);
                    _$setProp(_el$53, "paddingBottom", 0);
                    _$setProp(_el$53, "flexShrink", 0);
                    _$setProp(_el$54, "flexShrink", 0);
                    _$setProp(_el$54, "wrapMode", "none");
                    _$insert(_el$54, isActive ? [(() => {
                      var _el$55 = _$createElement("span"),
                        _el$56 = _$createTextNode(`● `);
                      _$insertNode(_el$55, _el$56);
                      _$insert(_el$55, displayAccountName, null);
                      _$effect(_$p => _$setProp(_el$55, "style", {
                        fg: COLORS.accent.teal,
                        bold: true
                      }, _$p));
                      return _el$55;
                    })(), (() => {
                      var _el$57 = _$createElement("span"),
                        _el$58 = _$createTextNode(` (ACTIVE)`);
                      _$insertNode(_el$57, _el$58);
                      _$effect(_$p => _$setProp(_el$57, "style", {
                        fg: COLORS.accent.teal
                      }, _$p));
                      return _el$57;
                    })()] : (() => {
                      var _el$60 = _$createElement("span");
                      _$insert(_el$60, displayAccountName);
                      _$effect(_$p => _$setProp(_el$60, "style", {
                        fg: COLORS.text.primary,
                        bold: true
                      }, _$p));
                      return _el$60;
                    })());
                    _$insert(_el$52, _$createComponent(For, {
                      each: accountQuotas,
                      children: quota => {
                        const rawDisplayLabel = quota.label.replace(accountName, "").replace(/^[\s-]+/, "").trim();
                        const displayLabel = rawDisplayLabel || "Status";
                        const compact = Boolean(props.twoColumns);
                        const labelWidth = compact ? 10 : 15;
                        const barWidth = compact ? 10 : 20;
                        const displayLabelText = compact ? truncateText(displayLabel, labelWidth) : displayLabel;
                        const resetText = formatResetTime(quota.resetAt, compact);
                        const compactResetText = resetText;
                        const errorText = compact ? truncateText(`${displayLabel}: ${quota.error ?? "Error"}`, 28) : `${displayLabel}: ${quota.error}`;
                        return _$createComponent(Show, {
                          get when() {
                            return !quota.error;
                          },
                          get fallback() {
                            return (() => {
                              var _el$69 = _$createElement("box"),
                                _el$70 = _$createElement("text"),
                                _el$71 = _$createTextNode(`✗ `);
                              _$insertNode(_el$69, _el$70);
                              _$setProp(_el$69, "paddingLeft", 1);
                              _$setProp(_el$69, "flexShrink", 0);
                              _$insertNode(_el$70, _el$71);
                              _$insert(_el$70, errorText, null);
                              _$effect(_$p => _$setProp(_el$70, "fg", COLORS.accent.red, _$p));
                              return _el$69;
                            })();
                          },
                          get children() {
                            var _el$61 = _$createElement("box"),
                              _el$62 = _$createElement("text"),
                              _el$63 = _$createElement("span"),
                              _el$64 = _$createElement("span"),
                              _el$65 = _$createElement("span"),
                              _el$66 = _$createTextNode(` `),
                              _el$67 = _$createElement("span"),
                              _el$68 = _$createTextNode(` `);
                            _$insertNode(_el$61, _el$62);
                            _$setProp(_el$61, "paddingLeft", 1);
                            _$setProp(_el$61, "flexShrink", 0);
                            _$insertNode(_el$62, _el$63);
                            _$insertNode(_el$62, _el$64);
                            _$insertNode(_el$62, _el$65);
                            _$insertNode(_el$62, _el$67);
                            _$setProp(_el$62, "wrapMode", "none");
                            _$insert(_el$63, () => padRight(displayLabelText, labelWidth));
                            _$insert(_el$64, () => renderBar(quota.used, barWidth));
                            _$insertNode(_el$65, _el$66);
                            _$insert(_el$65, () => padLeft((quota.used * 100).toFixed(0) + "%", 4), null);
                            _$insertNode(_el$67, _el$68);
                            _$insert(_el$67, compactResetText, null);
                            _$effect(_p$ => {
                              var _v$24 = {
                                  fg: COLORS.text.secondary
                                },
                                _v$25 = {
                                  fg: getColor(quota.used)
                                },
                                _v$26 = {
                                  fg: COLORS.text.primary
                                },
                                _v$27 = {
                                  fg: COLORS.text.muted
                                };
                              _v$24 !== _p$.e && (_p$.e = _$setProp(_el$63, "style", _v$24, _p$.e));
                              _v$25 !== _p$.t && (_p$.t = _$setProp(_el$64, "style", _v$25, _p$.t));
                              _v$26 !== _p$.a && (_p$.a = _$setProp(_el$65, "style", _v$26, _p$.a));
                              _v$27 !== _p$.o && (_p$.o = _$setProp(_el$67, "style", _v$27, _p$.o));
                              return _p$;
                            }, {
                              e: undefined,
                              t: undefined,
                              a: undefined,
                              o: undefined
                            });
                            return _el$61;
                          }
                        });
                      }
                    }), null);
                    return _el$52;
                  })();
                }
              }));
              _$effect(_p$ => {
                var _v$22 = props.twoColumns ? "row" : "column",
                  _v$23 = props.twoColumns ? 1 : 0;
                _v$22 !== _p$.e && (_p$.e = _$setProp(_el$51, "flexDirection", _v$22, _p$.e));
                _v$23 !== _p$.t && (_p$.t = _$setProp(_el$51, "gap", _v$23, _p$.t));
                return _p$;
              }, {
                e: undefined,
                t: undefined
              });
              return _el$51;
            })()
          }), null);
          _$effect(_$p => _$setProp(_el$46, "fg", COLORS.text.primary, _$p));
          return _el$43;
        })();
      }
    }), null);
    _$effect(_p$ => {
      var _v$18 = props.isSelected ? COLORS.accent.teal : COLORS.border,
        _v$19 = props.width ?? "49%",
        _v$20 = COLORS.bg.secondary,
        _v$21 = COLORS.accent.teal;
      _v$18 !== _p$.e && (_p$.e = _$setProp(_el$37, "borderColor", _v$18, _p$.e));
      _v$19 !== _p$.t && (_p$.t = _$setProp(_el$37, "width", _v$19, _p$.t));
      _v$20 !== _p$.a && (_p$.a = _$setProp(_el$37, "backgroundColor", _v$20, _p$.a));
      _v$21 !== _p$.o && (_p$.o = _$setProp(_el$40, "fg", _v$21, _p$.o));
      return _p$;
    }, {
      e: undefined,
      t: undefined,
      a: undefined,
      o: undefined
    });
    return _el$37;
  })();
}
function StatusBar(props) {
  const formatTime = date => {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };
  const daysLabel = () => {
    const days = props.daysFilter;
    if (days === 0) return "ALL";
    if (days === 1) return "TODAY";
    if (days === 7) return "WEEK";
    if (days === 30) return "MONTH";
    return days.toString();
  };
  return (() => {
    var _el$72 = _$createElement("box"),
      _el$73 = _$createElement("text"),
      _el$74 = _$createElement("span"),
      _el$75 = _$createTextNode(`⟳ `),
      _el$76 = _$createTextNode(` │ DAYS: `),
      _el$78 = _$createElement("span"),
      _el$79 = _$createTextNode(` │ `),
      _el$80 = _$createElement("span");
    _$insertNode(_el$72, _el$73);
    _$setProp(_el$72, "height", 1);
    _$setProp(_el$72, "border-top", true);
    _$setProp(_el$72, "paddingLeft", 1);
    _$setProp(_el$72, "paddingRight", 1);
    _$insertNode(_el$73, _el$74);
    _$insertNode(_el$73, _el$76);
    _$insertNode(_el$73, _el$78);
    _$insertNode(_el$73, _el$79);
    _$insertNode(_el$73, _el$80);
    _$setProp(_el$73, "wrapMode", "none");
    _$insertNode(_el$74, _el$75);
    _$insert(_el$74, () => formatTime(props.lastUpdate), null);
    _$insert(_el$78, daysLabel);
    _$insertNode(_el$80, _$createTextNode(`TAB:PANEL ↑↓:SCROLL ←→:DAYS t:TODAY w:WEEK m:MONTH a:ALL HOME:RESET ^C:EXIT`));
    _$effect(_p$ => {
      var _v$28 = COLORS.border,
        _v$29 = COLORS.bg.accent,
        _v$30 = COLORS.text.secondary,
        _v$31 = {
          fg: COLORS.accent.teal
        },
        _v$32 = {
          fg: COLORS.accent.teal
        },
        _v$33 = {
          fg: COLORS.text.muted
        };
      _v$28 !== _p$.e && (_p$.e = _$setProp(_el$72, "borderColor", _v$28, _p$.e));
      _v$29 !== _p$.t && (_p$.t = _$setProp(_el$72, "backgroundColor", _v$29, _p$.t));
      _v$30 !== _p$.a && (_p$.a = _$setProp(_el$73, "fg", _v$30, _p$.a));
      _v$31 !== _p$.o && (_p$.o = _$setProp(_el$74, "style", _v$31, _p$.o));
      _v$32 !== _p$.i && (_p$.i = _$setProp(_el$78, "style", _v$32, _p$.i));
      _v$33 !== _p$.n && (_p$.n = _$setProp(_el$80, "style", _v$33, _p$.n));
      return _p$;
    }, {
      e: undefined,
      t: undefined,
      a: undefined,
      o: undefined,
      i: undefined,
      n: undefined
    });
    return _el$72;
  })();
}
function Dashboard(props) {
  const [daysFilter, setDaysFilter] = createSignal(props.initialDays ?? 1);
  const [lastUpdate, setLastUpdate] = createSignal(new Date());
  const [allMessages, setAllMessages] = createSignal([]);
  const [cachedStats, setCachedStats] = createSignal(new Map());
  const [quotas, setQuotas] = createSignal([]);
  const [anthropicThresholds, setAnthropicThresholds] = createSignal(null);
  const [codexThresholds, setCodexThresholds] = createSignal(null);
  const [cursor, setCursor] = createSignal(createCursor());
  const [isFirstLoad, setIsFirstLoad] = createSignal(true);
  const [isFullyLoaded, setIsFullyLoaded] = createSignal(false);
  const [pageOffset, setPageOffset] = createSignal(0);
  const [internalScroll, setInternalScroll] = createSignal(0);
  const [selectedPanel, setSelectedPanel] = createSignal("usage");
  const dimensions = useTerminalDimensions();
  const maxVisibleDays = () => {
    const termHeight = dimensions().height;
    const headerHeight = 3;
    const statusBarHeight = 1;
    const contentHeight = termHeight - headerHeight - statusBarHeight - 2;
    const panelHeight = sideBySide() ? contentHeight : Math.floor(contentHeight / 2);
    const linesPerDay = 5;
    return Math.max(1, Math.floor((panelHeight - 6) / linesPerDay));
  };
  const displayedStats = () => cachedStats();
  const loadData = async () => {
    const storagePath = getOpenCodeStoragePath();
    if (isFirstLoad()) {
      const recentMessages = await loadRecentMessages(storagePath, 24, props.providerFilter);
      setAllMessages(recentMessages);
      const stats = aggregateByDate(recentMessages);
      setCachedStats(stats);
      setIsFirstLoad(false);
      setLastUpdate(new Date());
      setTimeout(async () => {
        const allMessages = await loadMessages(storagePath, props.providerFilter);
        setAllMessages(allMessages);
        const fullStats = aggregateByDate(allMessages);
        setCachedStats(fullStats);
        setIsFullyLoaded(true);
        setLastUpdate(new Date());
      }, 0);
    } else {
      const result = await loadMessagesIncremental(storagePath, cursor(), props.providerFilter);
      if (result.messages.length > 0) {
        const updated = [...allMessages(), ...result.messages];
        setAllMessages(updated);
        const stats = aggregateByDate(updated);
        setCachedStats(stats);
      }
      setCursor(result.cursor);
      setLastUpdate(new Date());
    }
  };
  const loadQuotas = async () => {
    const results = [];
    try {
      const thresholds = await loadMultiAccountThresholds();
      setAnthropicThresholds(thresholds);
      const multiAccount = await loadMultiAccountQuota();
      results.push(...multiAccount);
    } catch (err) {
      setAnthropicThresholds(null);
      results.push({
        source: "anthropic",
        label: "Multi-Account",
        used: 0,
        error: `Load error: ${err}`
      });
    }
    try {
      const antigravity = await loadAntigravityQuota();
      results.push(...antigravity);
    } catch (err) {
      results.push({
        source: "antigravity",
        label: "Antigravity",
        used: 0,
        error: `Load error: ${err}`
      });
    }
    try {
      const codexThresholdConfig = await loadCodexThresholds();
      setCodexThresholds(codexThresholdConfig);
      const codex = await loadCodexQuota();
      results.push(...codex);
    } catch (err) {
      setCodexThresholds(null);
      results.push({
        source: "codex",
        label: "Codex",
        used: 0,
        error: `Load error: ${err}`
      });
    }
    setQuotas(results);
  };
  onMount(async () => {
    await Promise.all([loadQuotas(), loadData()]);
    const intervalId = setInterval(async () => {
      await Promise.all([loadQuotas(), loadData()]);
    }, (props.refreshInterval ?? 300) * 1000);
    return () => clearInterval(intervalId);
  });
  useKeyboard(key => {
    const keyName = key.name?.toLowerCase() || key.sequence;
    if (keyName === "t") {
      setDaysFilter(1);
      setPageOffset(0);
      setInternalScroll(0);
    }
    if (keyName === "w") {
      setDaysFilter(7);
      setPageOffset(0);
      setInternalScroll(0);
    }
    if (keyName === "m") {
      setDaysFilter(30);
      setPageOffset(0);
      setInternalScroll(0);
    }
    if (keyName === "a") {
      setDaysFilter(0);
      setPageOffset(0);
      setInternalScroll(0);
    }
    if (keyName === "tab" || keyName === "\t") {
      setSelectedPanel(selectedPanel() === "usage" ? "quota" : "usage");
    }
    if (selectedPanel() === "usage") {
      const totalDays = cachedStats().size;
      const visibleDays = daysFilter() === 0 ? 10 : daysFilter();
      const scrollStep = visibleDays;
      const maxOffset = Math.max(0, totalDays - visibleDays);
      if (keyName === "left" || keyName === "\x1b[d") {
        setPageOffset(Math.min(pageOffset() + scrollStep, maxOffset));
        setInternalScroll(0);
      }
      if (keyName === "right" || keyName === "\x1b[c") {
        setPageOffset(Math.max(pageOffset() - scrollStep, 0));
        setInternalScroll(0);
      }
      if (keyName === "home") {
        setPageOffset(0);
        setInternalScroll(0);
      }
      const allEntries = Array.from(cachedStats().entries());
      const sortedByDateDesc = allEntries.sort((a, b) => b[0].localeCompare(a[0]));
      const afterScrollOffset = sortedByDateDesc.slice(pageOffset());
      const requestedDays = visibleDays;
      const totalInWindow = Math.min(afterScrollOffset.length, requestedDays);
      const maxInternalScroll = Math.max(0, totalInWindow - maxVisibleDays());
      if (keyName === "up" || keyName === "\x1b[a") {
        setInternalScroll(Math.max(internalScroll() - 1, 0));
      }
      if (keyName === "down" || keyName === "\x1b[b") {
        setInternalScroll(Math.min(internalScroll() + 1, maxInternalScroll));
      }
    }
  });
  const sideBySide = () => dimensions().width >= 168;
  const quotaTwoColumns = () => dimensions().width >= 150;
  return (() => {
    var _el$82 = _$createElement("box"),
      _el$83 = _$createElement("box");
    _$insertNode(_el$82, _el$83);
    _$setProp(_el$82, "width", "100%");
    _$setProp(_el$82, "height", "100%");
    _$setProp(_el$82, "flexDirection", "column");
    _$setProp(_el$83, "flexGrow", 1);
    _$setProp(_el$83, "width", "100%");
    _$setProp(_el$83, "gap", 0);
    _$setProp(_el$83, "padding", 0);
    _$insert(_el$83, _$createComponent(UsageTable, {
      get stats() {
        return displayedStats();
      },
      get pageOffset() {
        return pageOffset();
      },
      get daysFilter() {
        return daysFilter();
      },
      get internalScroll() {
        return internalScroll();
      },
      get maxVisibleDays() {
        return maxVisibleDays();
      },
      get isLoading() {
        return !isFullyLoaded();
      },
      get isSelected() {
        return selectedPanel() === "usage";
      },
      get width() {
        return sideBySide() ? "49%" : "100%";
      }
    }), null);
    _$insert(_el$83, _$createComponent(QuotaPanel, {
      get quotas() {
        return quotas();
      },
      get isSelected() {
        return selectedPanel() === "quota";
      },
      get width() {
        return sideBySide() ? "49%" : "100%";
      },
      get twoColumns() {
        return quotaTwoColumns();
      },
      get anthropicThresholds() {
        return anthropicThresholds();
      },
      get codexThresholds() {
        return codexThresholds();
      }
    }), null);
    _$insert(_el$82, _$createComponent(StatusBar, {
      get daysFilter() {
        return daysFilter();
      },
      get lastUpdate() {
        return lastUpdate();
      }
    }), null);
    _$effect(_p$ => {
      var _v$34 = COLORS.bg.primary,
        _v$35 = sideBySide() ? "row" : "column";
      _v$34 !== _p$.e && (_p$.e = _$setProp(_el$82, "backgroundColor", _v$34, _p$.e));
      _v$35 !== _p$.t && (_p$.t = _$setProp(_el$83, "flexDirection", _v$35, _p$.t));
      return _p$;
    }, {
      e: undefined,
      t: undefined
    });
    return _el$82;
  })();
}
export async function runSolidDashboard(options) {
  await render(() => _$createComponent(Dashboard, {
    get providerFilter() {
      return options.providerFilter;
    },
    get initialDays() {
      return _$memo(() => typeof options.initialDays === "number")() ? options.initialDays : 1;
    },
    get refreshInterval() {
      return _$memo(() => typeof options.refreshInterval === "number")() ? options.refreshInterval : 300;
    }
  }), {
    exitOnCtrlC: true,
    targetFps: 30,
    useMouse: false
  });
}